import { IndexedDbService } from '@zaoblako/web/core/services/indexed-db.service';
import { LazyAsyncOnceClass } from '@zaoblako/shared/common/classes/lazy-async-once.class';
import { environment } from '../../../environments/environment';

import moment from 'moment-timezone';
import { Injectable } from '@angular/core';
import { defer, Observable } from 'rxjs';

/** Database name in "IndexedDB" */
const DB_NAME = 'frm_http_cache';

/** Database version */
const DB_VERSION = 2;

/** Name of responses storage */
const STORE_NAME = 'responses';

/** Cached entry TTL in millisecnds */
const CACHE_TTL = 86400000;

/** Delay between expired entries drop running */
const CLEAR_EXPIRED_DELAY = 900000;

/** Cached response data */
export interface CachedResponse {
  /** Cached entry creation date and time */
  created: Date;
  /** "Last-Modified" header value (date in ISO-8601 format) */
  lastModified: string;
  /** HTTP status code */
  status: number;
  /** HTTP status text */
  statusText: string;
  /** HTTP response body */
  body: any;
}

/** Provide HTTP GET-requests caching using "Last-Modified" and "If-Modified-Since" headers */
@Injectable({
  providedIn: 'root'
})
export class RequestCacheService {
  private readonly init: LazyAsyncOnceClass<void>;

  public constructor(
    private readonly indexedDbService: IndexedDbService
  ) {
    this.init = new LazyAsyncOnceClass(() => this.initInternal());
  }

  /**
   * Get cached response by request URL
   *
   * @param urlWithParams Request URL with query parameters
   *
   * @returns Cached response or `undefined` if response not cached
   */
  public getResponse(urlWithParams: string): Observable<CachedResponse> {
    return defer(() => this.getResponseInternal(urlWithParams));
  }

  /**
   * Save response to cache
   *
   * @param urlWithParams Request URL with query parameters
   * @param response Reponse data
   */
  public setResponse(urlWithParams: string, response: CachedResponse): Observable<void> {
    return defer(() => this.setResponseInternal(urlWithParams, response));
  }

  /** Initialize service */
  private async initInternal(): Promise<void> {
    if (environment.debugLog) {
      console.debug('[RequestCacheService] Initialize...');
    }

    await this.clearExpired();
    this.scheduleNextClearExpired();

    if (environment.debugLog) {
      console.debug('[RequestCacheService] Ready to work.');
    }
  }

  /** Clear expired cached responses */
  private async clearExpired(): Promise<void> {
    const date = moment()
      .startOf('second')
      .toDate()
      .valueOf();
    if (environment.debugLog) {
      console.debug('[RequestCacheService] Try to delete expired cached entries...');
    }
    const db = await this.getDb();

    // Collect records to delete
    const toDelete: string[] = [];
    const collectExpiredTransaction = db.transaction(STORE_NAME, 'readonly');
    for await (const cursor of collectExpiredTransaction.store) {
      const needToDelete = (
        !(<CachedResponse>cursor.value)?.created ||
        (date - (<CachedResponse>cursor.value).created.valueOf() > CACHE_TTL)
      );
      if (needToDelete) {
        toDelete.push(cursor.key);
      }
    }
    await collectExpiredTransaction.done;
    if (!toDelete?.length) {
      if (environment.debugLog) {
        console.debug('[RequestCacheService] No cached HTTP responses to delete.');
      }
      return;
    }

    // Delete expired rows
    const deleteExpiredTransaction = db.transaction(STORE_NAME, 'readwrite');
    await Promise.all([
      ...toDelete.map((key) => deleteExpiredTransaction.store.delete(key)),
      deleteExpiredTransaction.done
    ]);
    if (environment.debugLog) {
      console.debug(`[RequestCacheService] ${toDelete.length} cached responses was deleted.`);
    }
  }

  /** Schedule next expired entries clearing */
  private scheduleNextClearExpired() {
    setTimeout(
      async (): Promise<void> => {
        await this.clearExpired();
        this.scheduleNextClearExpired();
      },
      CLEAR_EXPIRED_DELAY
    );
  }

  /**
   * Get cached response by request URL
   *
   * @param urlWithParams Request URL with query parameters
   *
   * @returns Cached response or `undefined` if response not cached
   */
  private async getResponseInternal(urlWithParams: string): Promise<CachedResponse> {
    await this.init.getValue();

    const db = await this.getDb();
    return db.get(STORE_NAME, urlWithParams);
  }

  /**
   * Save response to cache
   *
   * @param urlWithParams Request URL with query parameters
   * @param response Reponse data
   */
  private async setResponseInternal(urlWithParams: string, response: CachedResponse): Promise<void> {
    await this.init.getValue();

    const db = await this.getDb();
    await db.put(STORE_NAME, response, urlWithParams)
  }

  /** Get "IndexedDB" connector */
  private getDb() {
    return this.indexedDbService.getDatabase(DB_NAME, DB_VERSION, {
      // Something like migration, called when changing the database version
      upgrade: (db) => {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      }
    });
  }
}
